<template>
  <div
    class="editor-only-lyric height-all"
    :class="{
      'lyric-edit-mode': renderData.editMode,
      'editor-only-lyric--mobile': createRenderType == 'mobile',
      [`ditor-only-lyric--${props.type}`]: true,
    }"
  >
    <div class="lyric-edit">
      <LyricEdit
        ref="refLyricEdit"
        :placeholder="props.placeholder"
        :lyric="renderData.lyric"
        :isLoading="renderData.isLoading"
        @countChange="onCountChange"
        @input="onInput"
        @change="onChange"
        @blur="onBlur"
        @focus="onFocus"
        :maxLength="props.maxLength"
      ></LyricEdit>
    </div>

    <div class="lyric-footer flex">
      <div
        class="flex1 count"
        :class="{ 'count-error': renderData.charCount >= props.maxLength }"
      >
        {{ renderData.charCount }}/{{ props.maxLength }}
      </div>
      <div class="lyric-footer-btns">
        <div class="fn-inline lyric-footer-btn btn1">
          <Bubble
            :content="$$t('create.song_structure')"
            placement="top"
            :disabled="renderData.tagVisible"
          >
            <Button
              :renderStyle="{
                '--padding': '0 12px',
                '--width':
                  $$language() == 'cn'
                    ? '60px'
                    : createRenderType != 'mobile'
                    ? '40px'
                    : 'max-content',
                '--height': '100%',
                '--borderRadius': '50px',
                '--background': 'rgba(255, 255, 255, 0.1)',
                '--fontColor': '#fff',
                '--fontSize': '12px',
                '--fontWeight': 'normal',
                '--border': 'none',
                '--backgroundHover': 'rgba(255, 255, 255, 0.2)',
                '--fontColorHover': '#fff',
              }"
              :disabled="
                props.disabled ||
                renderData.isLoading ||
                renderData.charCount > props.maxLength - 10
              "
              @click.stop="toggleTagVisible"
            >
              <i
                class="block"
                v-if="$$language() != 'cn' && createRenderType != 'mobile'"
              ></i>
              <span class="block" v-else>{{
                $$t("create.song_structure_btn")
              }}</span>
            </Button>
          </Bubble>
          <div class="lyric-tag-items" v-if="renderData.tagVisible">
            <template v-for="(item, index) in getRenderSegTypes()" :key="index">
              <div
                class="lyric-tag-item tc pointer"
                @click.stop="
                  () => {
                    doAddTag(item);
                  }
                "
              >
                {{ item[`label_${$$language()}`] }}
              </div>
            </template>
          </div>
        </div>
        <div class="fn-inline lyric-footer-btn btn2">
          <Bubble :content="$$t('create.ai_generate_row')" placement="top">
            <Button
              :renderStyle="{
                '--padding': '0 12px',
                '--width':
                  $$language() == 'cn'
                    ? '62px'
                    : createRenderType != 'mobile'
                    ? '40px'
                    : 'max-content',
                '--height': '100%',
                '--borderRadius': '50px',
                '--background': 'rgba(255, 255, 255, 0.1)',
                '--fontColor': '#fff',
                '--fontSize': '12px',
                '--fontWeight': 'normal',
                '--border': 'none',
                '--backgroundHover': 'rgba(255, 255, 255, 0.2)',
                '--fontColorHover': '#fff',
              }"
              :loading="
                renderData.curGenerateType === 'row' && renderData.isLoading
              "
              :disabled="
                props.disabled ||
                renderData.isLoading ||
                renderData.charCount > props.maxLength - 10
              "
              @click="onContinueWritingClick"
            >
              <i
                class="block"
                v-if="$$language() != 'cn' && createRenderType != 'mobile'"
              ></i>
              <span class="block" v-else>{{
                $$t("create.ai_generate_row_btn")
              }}</span>
              <!-- Continue writing -->
            </Button>
          </Bubble>
        </div>
        <div
          class="fn-inline lyric-footer-btn btn3"
          v-if="props.type != 'regionEditing'"
        >
          <Bubble
            :content="
              renderData.lyric
                ? $$t('create.ai_generate_lyrics')
                : $$t('create.ai_generate_random_lyrics')
            "
            placement="top"
            :disabled="createRenderType == 'mobile'"
          >
            <Button
              :renderStyle="{
                '--padding': '0 12px',
                '--width':
                  $$language() == 'cn'
                    ? '62px'
                    : createRenderType != 'mobile'
                    ? '40px'
                    : renderData.lyric
                    ? '112px'
                    : 'max-content',
                '--height': '100%',
                '--borderRadius': '50px',
                '--background': 'rgba(255, 255, 255, 0.1)',
                '--fontColor': '#fff',
                '--fontSize': '12px',
                '--fontWeight': 'normal',
                '--border': 'none',
                '--backgroundHover': 'rgba(255, 255, 255, 0.2)',
                '--fontColorHover': '#fff',
              }"
              :loading="
                renderData.curGenerateType === 'all' && renderData.isLoading
              "
              :disabled="
                props.disabled ||
                renderData.isLoading ||
                renderData.charCount > props.maxLength - 500
              "
              @click="onWriteAllLyricClick"
            >
              <i
                class="block"
                v-if="$$language() != 'cn' && createRenderType != 'mobile'"
              ></i>
              <span v-else class="block">{{
                renderData.lyric
                  ? $$t("create.ai_generate_lyrics")
                  : $$t("create.ai_generate_random_lyrics")
              }}</span>
              <!-- Write a lyric -->
            </Button>
          </Bubble>
        </div>
      </div>
    </div>

    <div
      class="lyric-box-mask"
      v-if="renderData.isLoading || props.disabled"
    ></div>
  </div>
</template>

<script setup>
import {
  ref,
  defineProps,
  defineEmits,
  computed,
  readonly,
  reactive,
  watch,
  defineExpose,
  onMounted,
  onUpdated,
  onUnmounted,
  getCurrentInstance,
} from "vue";
import Bubble from "@/components/bubble/bubble.vue";
import LyricEdit from "@/page/create/editLyric/inputLyric.vue";
import Button from "@/components/basic/button/Button.vue";
import { getSegTypes } from "@/config/const.js";
import feedListApi from "@/api/api/feedListApi.js";
import message from "@/components/functionCallComponent/message.jsx";
import globalConfig from "@/config/config.js";
import useCreateLayoutStore from "@/store/create/createLayoutStore.js";
const createLayoutStore = useCreateLayoutStore();
const { createRenderType } = storeToRefs(createLayoutStore);
import useUserDataStore from "@/store/userDataStore.js";
const globalProperties =
  getCurrentInstance()?.appContext.config.globalProperties;
const { $$language, $$t, $$tm, $$tValue } = getCurrentInstance().appContext?.config.globalProperties.$i18n ?? {}

const lyricDataStore = {};

const userDataStore = useUserDataStore();

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
  maxLength: {
    type: Number,
    default: 500,
  },
  lyric: {
    type: String,
    default: "",
  },
  type: {
    type: String,
    default: null,
  },
  placeholder: {
    type: String,
    default: null,
  },
  hasChange: {
    type: Boolean,
    default: false,
  },
  getBeforeLyrics: {
    type: Function,
    default: null,
  },
});

const emits = defineEmits(["change"]);
const refLyricEdit = ref(null);
const renderData = reactive({
  lyric: props.lyric,
  charCount: 0,
  isLoading: false,
  tagVisible: false,
  curGenerateType: "",
  editMode: false,
});

const getRenderSegTypes = () => {
  if (props.type == "extendEditing") {
    return getSegTypes().slice(1);
  } else {
    return getSegTypes();
  }
};

const onInput = async (value) => {
  if (!(await userDataStore.checkLoginStatus())) {
    return;
  }
  renderData.lyric = value;
};

const onChange = (value) => {
  renderData.lyric = value;
};

const onBlur = (count) => {
  renderData.editMode = false;
};

const onFocus = (count) => {
  renderData.editMode = true;
};

const onCountChange = (count) => {
  renderData.charCount = count;
};

const toggleTagVisible = async () => {
  if (!(await userDataStore.checkLoginStatus())) {
    return;
  }
  renderData.tagVisible = !renderData.tagVisible;
};

const doAddTag = (tag) => {
  refLyricEdit.value.insertTag(tag);
  renderData.tagVisible = false;
  globalProperties.logClickEvent("create_structure", {
    add_tag_type: tag,
    type: userDataStore.isLogin ? "sign_in" : "not_sign_in",
  });
};

const onClickOutside = async () => {
  renderData.tagVisible = false;
};

const onContinueWritingClick = async () => {
  renderData.tagVisible = false;
  globalProperties.logClickEvent("create_ailyrics", {
    type: userDataStore.isLogin ? "sign_in" : "not_sign_in",
  });

  if (!(await userDataStore.checkLoginStatus())) {
    return;
  }

  if (renderData.isLoading) {
    return;
  }
  renderData.isLoading = true;
  const params = {
    lyrics:
      (props.getBeforeLyrics?.() ?? "") +
      (refLyricEdit.value?.getBeforeLyrics() ?? ""),
    create_title: false,
    type: 1,
    song_id: props.item?.song_id,
  };
  renderData.curGenerateType = "row";
  let timeStamp = new Date().getTime();
  renderData.timeStamp = timeStamp;
  feedListApi
    .generateLyrics(params)
    .then((res) => {
      renderData.isLoading = false;
      if (renderData.timeStamp != timeStamp) {
        return;
      }
      const { code, data, msg } = res;
      if (code == 200) {
        refLyricEdit.value?.insertLyric(data.lyrics);
      } else {
        message.error({
          position: "top",
          content: $$t("create.ai_generate_lyric_failed"),
        });
      }
    })
    .catch((e) => {
      renderData.isLoading = false;
      if (renderData.timeStamp != timeStamp) {
        return;
      }
      message.error({
        position: "top",
        content: $$t("create.ai_generate_lyric_failed"),
      });
    });
};
const onWriteAllLyricClick = async () => {
  renderData.tagVisible = false;

  globalProperties.logClickEvent("create_ailyricsall", {
    type: userDataStore.isLogin ? "sign_in" : "not_sign_in",
  });
  if (!(await userDataStore.checkLoginStatus())) {
    return;
  }

  if (renderData.isLoading) {
    return;
  }
  renderData.isLoading = true;

  const params = {
    lyrics:
      (props.getBeforeLyrics?.() ?? "") +
      (refLyricEdit.value?.getBeforeLyrics() ?? ""),
    create_title: false,
    type: 4,
    song_id: props.item?.song_id,
  };
  renderData.curGenerateType = "all";
  let timeStamp = new Date().getTime();
  renderData.timeStamp = timeStamp;
  await feedListApi
    .generateLyrics(params)
    .then((res) => {
      renderData.isLoading = false;
      if (renderData.timeStamp != timeStamp) {
        return;
      }
      const { code, data, msg } = res;
      if (code == 200) {
        refLyricEdit.value?.insertLyric(data.lyrics);
      } else {
        message.error({
          position: "top",
          content: $$t("create.ai_generate_lyric_failed"),
        });
      }
    })
    .catch((e) => {
      renderData.isLoading = false;
      if (renderData.timeStamp != timeStamp) {
        return;
      }
      message.error({
        position: "top",
        content: $$t("create.ai_generate_lyric_failed"),
      });
    });
};



defineExpose({
  getLyrics() {
    return renderData.lyric;
  },
});

watch(
  () => {
    return props.lyric;
  },
  () => {
    renderData.lyric = props.lyric;
    renderData.timeStamp = new Date().getTime();
    renderData.isLoading = false;
  }
);

watch(
  () => renderData.lyric,
  (newVal) => {
    emits("change", renderData.lyric, renderData.charCount);
  }
);
onMounted(() => {
  document.addEventListener("click", onClickOutside);
});
onBeforeUnmount(() => {
  document.removeEventListener("click", onClickOutside);
})
</script>
<style lang="scss" scope>
.editor-only-lyric {
  padding: 16px 16px 16px 16px;
  border-radius: 12px;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0);
  position: relative;
  .lyric-box-mask {
    position: absolute;
    height: 100%;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    cursor: not-allowed;
  }
  &:hover {
    background-color: rgba(255, 255, 255, 0.125);
  }
  &.lyric-edit-mode {
    border: 1px solid #94adff;
    background-color: rgba(148, 173, 255, 0.1);
  }
  .lyric-edit {
    height: calc(100% - 30px - 8px);
  }
  .lyric-footer {
    padding: 8px 0 0 0;
    align-items: end;
    .count {
      font-family: HarmonyOS Sans SC;
      font-size: 12px;
      font-weight: normal;
      line-height: normal;
      letter-spacing: 0em;
      color: rgba(255, 255, 255, 0.5);
      vertical-align: bottom;
      width: 0;
    }
    .count-error {
      color: #ff4d4f;
    }
    .lyric-footer-btn {
      margin-left: 8px;
      height: 32px;
      .custom-button .el-button {
        padding: 0 12px;
      }
      i {
        height: 16px;
        width: 16px;
        background-color: rgba(255, 255, 255, 0.5);
      }
      .el-button.is-loading {
        span {
          display: none;
        }
        i.is-loading {
          display: block;
          height: 16px;
          width: 16px;
          background: url(@/assets/img/loading-mini-3x.png) !important;
          background-size: 100% 100% !important;
          animation: animation-rotate-infinite 0.75s infinite normal linear;
          svg {
            display: none;
          }
        }
      }
    }
    .btn1 {
      position: relative;
      i {
        background: url(@/assets/img/lyric-tag-ico.png);
        background-size: cover;
      }
    }
    .btn2 {
      i {
        background: url(@/assets/img/lyric-row-ico.png);
        background-size: cover;
      }
    }
    .btn3 {
      i {
        background: url(@/assets/img/lyric-whole-ico.png);
        background-size: cover;
      }
    }

    .lyric-tag-items {
      background: rgba(255, 255, 255, 0.2);
      border: 1px solid rgba(255, 255, 255, 0);
      backdrop-filter: blur(20px);
      width: 128px;
      border-radius: 8px;
      position: absolute;
      bottom: 40px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 100;
      .lyric-tag-item {
        line-height: 42px;
        font-family: HarmonyOS Sans SC;
        font-size: 14px;
        font-weight: 500;
        letter-spacing: 0em;
        color: #fff;
        &:first-child {
          border-radius: 8px 8px 0 0;
        }
        &:last-child {
          border-bottom: none;
          border-radius: 0 0 8px 8px;
        }
        &:hover {
          background: rgba(255, 255, 255, 0.1);
        }
      }
    }
  }
  &.editor-only-lyric--mobile {
    min-height: 200px;
    .lyric-footer-btn {
      margin-left: 8px;
      height: 32px;
      .custom-button .el-button {
        padding: 0 12px;
      }
    }
    @media screen and (max-width: 450px) {
      &.ditor-only-lyric--extendEditing {
        display: flex;
        flex-direction: column;
        .lyric-edit {
           flex: 1 1 0;
           height: auto;
        }
        .lyric-footer {
          flex-direction: column;
          align-items: end;
          min-height: 64px;
          margin-bottom: -8px;
          .count {
            width: 100%;
            text-align: right;
            margin-bottom: 10px;
          }
          .lyric-footer-btns{
             display: block;
             width: 100%;
          }
          .lyric-footer-btn{
            margin: 0 8px 8px 0;
          }
          .el-button {
            font-size: 10px !important;
            width: max-content !important;
          }
        }
      }
    }
  }
}
</style>
